import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { BulbOutlined } from '@ant-design/icons';
import { TOUR_ANCHOR } from '../../../../welcome-tour/tourAnchors';
import { CHECKOUT_COPY, CYCLE_SUFFIX } from 'lib/billing';
import { formatCents } from '../format';
import { BillingCycleTabs } from './BillingCycleTabs';
import { Tiers } from './Tiers';
import { AddonsSection } from './AddonsSection';
import { TrialChoice } from './TrialChoice';
import { TotalSummary } from './TotalSummary';
import { CurrentPlanBanner } from './CurrentPlanBanner';
import { ProceedButton } from './ProceedButton';
import { StepHeader } from '../StepHeader';
import { NotePanel } from '../NotePanel';
import { useCheckoutActions, useCheckoutState } from '../store';

// Gold used by the min-accounts notice and confirm dialog. Mirrors the
// marketing site's tc-gold tokens (gold-mid fill, gold-ink text — white on
// gold fails contrast). Local because theme9's `warning` is #E67E22 (orange),
// not yellow.
const GOLD = { fill: '#f5c544', hover: '#d4a017', ink: '#3d2a05' };

export function ConfigureStep() {
  const state = useCheckoutState();
  const actions = useCheckoutActions();
  const [confirmSingleAccount, setConfirmSingleAccount] = React.useState(false);

  const COPY = state.isModifyingExisting ? CHECKOUT_COPY.configureModify : CHECKOUT_COPY.configure;
  // Enabled when there is *something* to buy / modify: tier accounts, analyzer addon,
  // or at least one DE env. Pure modify-DE flows would otherwise stay disabled because
  // `totalAccounts` only counts copier tiers.
  const hasTierAccounts = (state.totalAccounts || 0) > 0;
  const hasAnalyzer = Boolean(state.analyzerOn);
  const hasDedicated = Boolean(state.dedicatedActive) && (state.dedicatedEnvCount || 0) > 0;
  const hasGrandTotal = (state.grandTotalCycleCents || 0) > 0;
  // For modify flows, also require a real plan diff. Prevents the CTA being
  // enabled when selection matches the user's current subscriptions exactly.
  const noPlanChanges = state.isModifyingExisting && state.hasPlanChanges === false;
  // A new Dedicated Environment is its own subscription/payment and can't be
  // checked out alongside other changes (the checkout confirms one payment).
  const dedicatedNeedsOwnCheckout = Boolean(state.dedicatedNeedsOwnCheckout);
  // Copying runs from a source account to a destination account, so a lone CFD
  // account has nothing to copy to.
  //
  // There is exactly ONE exclusion: Futures. One CFD + one Futures is a genuine
  // source/destination pair (futures-to-CFD copying), so that cart can copy.
  //
  // There is deliberately NO Analyzer exclusion. Every analyzer flag in this
  // store is derived from OWNERSHIP, not from cart intent:
  //   • analyzerActive / analyzerOn = `subscribed || (hasCopier && selected)`
  //     (useCheckoutCompat.ts:224)
  //   • analyzerEnabled is seeded from owned subscriptions on every drawer open
  //     — CheckoutProvider hydrates via selectionFromAllSubscriptions, and
  //     selectionForCycle sets `analyzerEnabled: hasAnalyzer`
  //     (selectionFromSubscription.js:251)
  // Gating on any of them silently hides this advisory from every existing
  // Analyzer subscriber. And it would be the wrong call regardless: owning
  // Analyzer does not make a single copier account able to copy. The
  // Analyzer-only case is already covered, since it has no copier accounts and
  // therefore never reaches `cfdAccounts === 1`.
  //
  // ADVISORY — deliberately absent from `disabled` below, because a single
  // copier account is still a legitimate purchase.
  const cfdAccounts = (state.standardCount || 0) + (state.premiumCount || 0);
  const showMinCopierAccounts = cfdAccounts === 1 && !state.wantsFutures;
  const addSecondAccount = () => {
    // Bump whichever tier the single account is on — hardcoding standard would
    // silently add an unrelated Standard account to a Premium-only cart.
    if ((state.premiumCount || 0) === 1) actions.setPremiumCount(2);
    else actions.setStandardCount(2);
  };
  const disabled =
    (!hasTierAccounts && !hasAnalyzer && !hasDedicated && !hasGrandTotal) ||
    noPlanChanges ||
    dedicatedNeedsOwnCheckout ||
    state.catalogLoading ||
    !!state.catalogError;

  return (
    <Stack spacing={2.75}>
      <StepHeader copy={COPY} />

      <CurrentPlanBanner />
      {/* Wrappers exist so the welcome tour can spotlight each section without
          any of these components needing to know about it. */}
      <Box data-tour={TOUR_ANCHOR.CHECKOUT_CYCLE}>
        <BillingCycleTabs />
      </Box>
      <Stack spacing={2.25}>
        <Box data-tour={TOUR_ANCHOR.CHECKOUT_TIERS}>
          <Tiers />
        </Box>
        <Box data-tour={TOUR_ANCHOR.CHECKOUT_ADDONS}>
          <AddonsSection />
        </Box>
      </Stack>
      <TrialChoice />
      <Box data-tour={TOUR_ANCHOR.CHECKOUT_TOTAL}>
        <TotalSummary />
      </Box>

      {state.catalogLoading && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            {COPY.catalogLoading}
          </Typography>
        </Stack>
      )}
      {state.catalogError && <NotePanel severity="error" body={COPY.catalogError} />}
      {dedicatedNeedsOwnCheckout && <NotePanel severity="info" body={CHECKOUT_COPY.addons.dedicatedSeparateCheckout} />}
      {showMinCopierAccounts && (
        // Informational, not a warning — the amber treatment read as an alarm
        // about the user's own setup. Still weighted to survive being skimmed
        // past: solid icon disc, bold title, filled action button.
        <Alert
          severity="info"
          icon={
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: GOLD.fill,
                color: GOLD.ink,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12
              }}
            >
              <BulbOutlined />
            </Box>
          }
          sx={(t) => ({
            alignItems: 'flex-start',
            borderRadius: 1.5,
            border: `1px solid ${t.palette.divider}`,
            bgcolor: 'background.paper',
            '& .MuiAlert-icon': { mt: 0.25 }
          })}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.primary' }}>
            {CHECKOUT_COPY.minCopierAccounts.title}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
            {CHECKOUT_COPY.minCopierAccounts.body}
          </Typography>
          {/* Same affordance as the "Have a discount code?" prompt in the order
              summary (TotalSummary DiscountEntry): a dotted-underline text
              action, so the two quiet in-card actions in this checkout read as
              one family. */}
          <Typography
            component="button"
            type="button"
            variant="caption"
            onClick={addSecondAccount}
            sx={{
              display: 'block',
              mt: 0.75,
              p: 0,
              border: 0,
              background: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: 11.5,
              fontWeight: 600,
              color: 'text.secondary',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textUnderlineOffset: 3,
              '&:hover': { color: 'primary.main' }
            }}
          >
            {CHECKOUT_COPY.minCopierAccounts.action}
          </Typography>
        </Alert>
      )}

      {/* Sticky checkout bar: the configure list is long enough that the CTA
          (and the number it commits to) used to live below the fold — you
          tweaked seats at the top with no idea what the plan now costs.
          Sticks to the drawer body's bottom edge; negative margins cancel the
          body padding so the bar spans edge to edge. */}
      <Box
        sx={(theme) => ({
          position: 'sticky',
          bottom: -24,
          mx: -3,
          mb: -3,
          px: 3,
          py: 1.5,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(16, 24, 21, 0.92)' : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(8px)'
        })}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
            Plan total
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 18, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            {formatCents(state.grandTotalCycleCents || 0)}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {(CYCLE_SUFFIX as any)[state.billingCycle as string] || '/mo'}
            </Typography>
          </Typography>
        </Box>
        <ProceedButton
          label={COPY.cta}
          disabled={disabled}
          onClick={() => {
            // The inline advisory above is easy to scroll past, so the question is
            // asked again here — at the point the buyer actually commits. Still a
            // question, not a gate: answering it proceeds.
            if (showMinCopierAccounts) {
              setConfirmSingleAccount(true);
              return;
            }
            actions.goToPayment();
          }}
        />
      </Box>

      {/* The checkout Drawer's Modal root sits at zIndex.drawer + 1000 (2200,
          CheckoutDrawer.tsx:24). A Dialog defaults to zIndex.modal (1300) and
          would render UNDERNEATH it — invisible, while still blocking the CTA
          from navigating, so Checkout would look dead. Lift above the drawer,
          matching CancelConfirmModal.tsx:23 and CryptoElement.tsx:353. */}
      <Dialog
        open={confirmSingleAccount}
        onClose={() => setConfirmSingleAccount(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (t) => t.zIndex.drawer + 1100 }}
      >
        <DialogTitle sx={{ fontSize: 16.5, fontWeight: 600 }}>{CHECKOUT_COPY.minCopierAccounts.confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>{CHECKOUT_COPY.minCopierAccounts.confirmBody}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          {/* Adding the pair is the outcome we want, so it carries the filled
              gold weight; continuing with one stays available but quiet. */}
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              setConfirmSingleAccount(false);
              actions.goToPayment();
            }}
            sx={{ textTransform: 'none' }}
          >
            {CHECKOUT_COPY.minCopierAccounts.confirmProceed}
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => {
              setConfirmSingleAccount(false);
              addSecondAccount();
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 5,
              bgcolor: GOLD.fill,
              color: GOLD.ink,
              '&:hover': { bgcolor: GOLD.hover, color: GOLD.ink }
            }}
          >
            {CHECKOUT_COPY.minCopierAccounts.action}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
